import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  ChevronRight,
  Database,
  Download,
  ExternalLink,
  Layers,
  LogOut,
  Moon,
  RefreshCw,
  Search,
  Settings2,
  Sun,
  TriangleAlert,
  Users,
  X,
} from "lucide-react";
import useDevTheme from "./useDevTheme";
import SectionHead from "./DevSectionHead";
import ContainerSelect from "./ContainerSelect";
import "./DeveloperDashboard.css";

/**
 * The developer dashboard — /dev/dashboard.
 *
 * Its OWN component and its OWN stylesheet, sharing nothing with
 * AdminDashboard.js. The admin pages are in front of a live user and the two
 * roles have separate access by design, so there is nothing here that can drift
 * into them and nothing there that constrains this.
 *
 * WHAT IT SHOWS
 * Every slice of the stored history that can be downloaded, as cards that say
 * what is in each file and how many rows it has, so a download is a decision
 * rather than a guess. Four scopes: the whole database, one account, one pair
 * date, one account usage.
 *
 * ONE REQUEST DRAWS THE WHOLE PAGE
 * GET /api/dev/export/inventory returns the counts AND the download URLs for
 * every card. The UI never builds a URL from string pieces — if a scope or a
 * shape changes, it changes in one place on the server. The alternative was
 * template strings in here, which is the same mistake as the period-key list
 * that lived in six copies until one of them was missed.
 *
 * DOWNLOADS GO THROUGH fetch, NOT A LINK
 * These endpoints need `Authorization: Bearer <token>`, and an <a href> cannot
 * send a header — a plain link would return 401 and save a file containing the
 * error. So each button fetches, checks the status, and only then turns the body
 * into a blob and saves it. A failure shows the server's actual message instead
 * of writing a corrupt spreadsheet to disk.
 *
 * THEME
 * DARK BY DEFAULT, light if the reader asks for it, and the choice is
 * remembered. Same shape as InvestorReport.js — a class on the root element,
 * with the full palette on the bare root and only what changes redefined for
 * dark. The theme is decided here and written to that class rather than left to
 * a media query, so no colour has its only definition inside one; a token that
 * exists only under `prefers-color-scheme` is invisible to the other theme and
 * to the toggle.
 *
 * SEARCH
 * One box filters all three card sections at once. It matches against a string
 * built per card from everything that card is identified by — login, usage id,
 * pair date, broker name, currency, host, leverage, open/closed, and every
 * timestamp in BOTH the stored form ("2026-08-24 14:37:31") and the displayed
 * one ("24 Aug 14:37") — so typing what you can see works as well as typing what
 * is in the database. Whitespace splits the query into terms that must ALL
 * match, which is what makes "7000103 08-24" narrow instead of widen.
 *
 * Filtering happens in the browser over the inventory already loaded. There is
 * no request per keystroke, and no debounce needed for it either.
 *
 * The route guard is in App.js (ProtectedRoute requiredRole="developer"). That
 * guard is CLIENT-SIDE: it decides what renders, not what the server answers.
 * The endpoints behind every button are guarded independently with
 * `protect + isDeveloper`.
 */

/**
 * The shapes each card can be downloaded as, in the order they are listed on a
 * card. `key` matches the inventory's `downloads` object and the server's
 * ?only= parameter; `count` names the field in the card's `counts` object, or is
 * null for a shape that always has content.
 */
const SHAPES = [
  {
    key: "usages",
    label: "Account details",
    count: "usages",
    hint: "one row per account usage — 27 columns, opening balance and rollups",
  },
  { key: "trades", label: "Trade log", count: "trades", hint: "closed trades only, nothing else" },
  { key: "cash", label: "Cash movements", count: "cash", hint: "deposits and withdrawals" },
  {
    key: "settlements",
    label: "Settlements",
    count: "settlements",
    hint: "per withdrawal: profit banked vs capital returned",
  },
  {
    key: "everything",
    label: "Everything",
    count: null,
    hint: "all of the above, each on its own sheet",
  },
];

const nf = (n) => Number(n || 0).toLocaleString();
const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "2026-08-24 14:37:31" -> "24 Aug 14:37". Full value stays in the title. */
function shortWhen(s) {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  if (!m) return s;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(m[3])} ${months[Number(m[2]) - 1]} ${m[4]}:${m[5]}`;
}

/**
 * Everything a card can be found by, lower-cased into one string.
 *
 * Dates go in twice, raw and shortened, because the card shows one form and the
 * database holds the other; a search that only matched the stored form would
 * fail on exactly the text the reader is looking at.
 */
function hay(parts) {
  return parts
    .filter((p) => p !== null && p !== undefined && p !== "")
    .join(" ")
    .toLowerCase();
}

/** Both forms of a timestamp, for the haystack. */
const whenParts = (s) => [s, shortWhen(s)];

/**
 * Every term must match. Splitting on whitespace and requiring all of them is
 * what lets a second word narrow the result — with OR, typing more would return
 * more, which is the opposite of what a search box is for.
 */
function matches(haystack, terms) {
  for (const t of terms) if (haystack.indexOf(t) === -1) return false;
  return true;
}

/* ---------------------------------------------------------------------------
   Download rows
   --------------------------------------------------------------------------- */

/**
 * One shape's row: its name, its row count, and its download button.
 *
 * A count of zero DISABLES the button rather than handing over a file with a
 * header row and nothing under it. An empty spreadsheet is indistinguishable
 * from a broken export, and most usages genuinely have no settlements. The row
 * stays visible and dimmed, because "no settlements" is information.
 *
 * Excel only. The server still accepts `?format=csv` — it is tested and costs
 * nothing to leave — but nothing here offers it, because one file format is one
 * fewer thing to choose between when every one of these is a spreadsheet.
 */
function ShapeRow({ shape, counts, downloads, onDownload, busy }) {
  const total = shape.count === null ? null : Number((counts && counts[shape.count]) || 0);
  const has = shape.count === null ? true : total > 0;
  const urls = (downloads && downloads[shape.key]) || {};

  return (
    <div className={`dev-shape${has ? "" : " dev-shape-empty"}`}>
      <span className="dev-shape-label" title={shape.hint}>
        {shape.label}
      </span>
      {total !== null && <span className="dev-pill">{nf(total)}</span>}

      <span className="dev-actions">
        <button
          type="button"
          className="dev-btn"
          disabled={!has || busy === urls.xlsx}
          onClick={() => onDownload(urls.xlsx)}
          title={`Download ${shape.label} as Excel`}
        >
          <Download size={13} aria-hidden="true" />
          <span>{busy === urls.xlsx ? "…" : "Excel"}</span>
        </button>
      </span>
    </div>
  );
}

function ShapeList({ counts, downloads, onDownload, busy, extra }) {
  return (
    <div className="dev-shapes">
      {SHAPES.map((s) => (
        <ShapeRow
          key={s.key}
          shape={s}
          counts={counts}
          downloads={downloads}
          onDownload={onDownload}
          busy={busy}
        />
      ))}
      {extra}
    </div>
  );
}

/**
 * A section heading with its own count.
 *
 * While a search is running the count reads "3 of 9" rather than "3", so a
 * filtered section can never be mistaken for a section that only ever had three
 * things in it.
 */
/** Shown in place of an empty grid, so a filter that matched nothing says so. */
function NoMatches({ what }) {
  return <p className="dev-empty">No {what} match this search.</p>;
}

/* ---------------------------------------------------------------------------
   The page
   --------------------------------------------------------------------------- */

const DeveloperDashboard = () => {
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [query, setQuery] = useState("");

  /* Dark by default, light if asked for, remembered — in useDevTheme so the two
     developer pages cannot end up with two different defaults. */
  const { theme, toggle, dark } = useDevTheme();

  /* Read through a try/catch: a hand-edited or half-cleared localStorage would
     otherwise throw inside render and blank the page rather than sending anyone
     back to the login screen. */
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  }, []);

  const token = localStorage.getItem("token");

  /**
   * WHICH CONTAINER THIS PAGE IS SHOWING.
   *
   * A container is a complete book: this page keeps every card, every count and
   * every detailed export it already had, and the dropdown only says whose
   * history fills them. "All" is every container together, which is what this
   * page showed before containers existed.
   *
   * Held in the URL — the choice is per viewer, a container becomes a link that
   * can be sent, and a reload comes back where it was. Empty means "let the
   * server pick", and it picks the DEFAULT container, never All.
   */
  const [container, setContainer] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("container") || "";
  });

  const chooseContainer = useCallback((next) => {
    setContainer(next);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (next) url.searchParams.set("container", next);
    else url.searchParams.delete("container");
    window.history.replaceState({}, "", url);
  }, []);

  /* Named in the two link titles below, so each says which container it will
     actually open rather than leaving the reader to work it out. */
  const selectedName =
    (inv && inv.selection && inv.selection.containerName) ||
    (inv && inv.selection && inv.selection.mode === "all" ? "all containers" : "") ||
    "";
  const defaultName =
    (inv && (inv.containers || []).find((c) => c.isDefault) && (inv.containers || []).find((c) => c.isDefault).name) ||
    "";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `/api/dev/export/inventory${container ? `?container=${encodeURIComponent(container)}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        /* 401 means the token is gone or expired, 403 means it is not a
           developer token. Both are worth saying plainly rather than showing an
           empty page that looks like there is no data. */
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `request failed (${res.status})`);
      }
      setInv(await res.json());
    } catch (err) {
      setError(err.message || "could not load the inventory");
    } finally {
      setLoading(false);
    }
    /* `container` IS A DEPENDENCY. Without it the callback closes over the
       container it was created with, so choosing another would rewrite the URL,
       move the dropdown, and leave every card on screen showing the old
       container's counts — with its download links still pointing there too. */
  }, [token, container]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Fetch one export and save it.
   *
   * The filename comes from the server's Content-Disposition so the two files
   * from one card cannot collide; if that header is missing for any reason the
   * URL still yields a usable name rather than "download".
   */
  const onDownload = useCallback(
    async (url) => {
      if (!url) return;
      setBusy(url);
      setError("");
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || body.error || `download failed (${res.status})`);
        }

        const disp = res.headers.get("Content-Disposition") || "";
        const match = /filename="([^"]+)"/.exec(disp);
        const name = match ? match[1] : url.replace(/^.*\//, "").replace(/\?.*$/, "") || "export";

        const blob = await res.blob();
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Revoke on the next tick; revoking synchronously can cancel the save in
        // some browsers before it has read the blob.
        setTimeout(() => URL.revokeObjectURL(href), 0);
      } catch (err) {
        setError(err.message || "download failed");
      } finally {
        setBusy("");
      }
    },
    [token]
  );

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };

  /**
   * The filtered view of the inventory.
   *
   * Built with useMemo keyed on the inventory and the query, so the haystacks
   * are assembled once per load rather than once per keystroke. A pair date's
   * haystack borrows the broker names of the accounts in it — the card shows
   * only logins, but "honor" ought to find the pairs Honor traded in.
   */
  const view = useMemo(() => {
    const empty = { accounts: [], pairs: [], pairDates: [], usages: [], matched: 0, active: false };
    if (!inv) return empty;

    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const active = terms.length > 0;

    /* What each usage contributes to a card that CONTAINS it. An account and a
       pair date are groupings, so a term that describes one of their usages
       should find them too: without this, typing "current" or a broker host
       matched the usage cards and left "0 of 5" over the accounts, which reads
       as a broken filter rather than as a deliberate distinction. */
    const extrasOf = new Map(
      inv.usages.map((u) => [
        u.usageId,
        [u.account, u.currency, u.role, u.host, u.leverage, u.current ? "current open" : "closed"],
      ])
    );
    const extrasFor = (ids) => ids.flatMap((id) => extrasOf.get(id) || []);

    const accounts = inv.accounts.filter((a) =>
      !active ||
      matches(
        hay([
          a.login,
          a.account,
          ...a.usageIds,
          ...a.pairStartDates,
          ...extrasFor(a.usageIds),
          ...whenParts(a.firstClose),
          ...whenParts(a.lastClose),
        ]),
        terms
      )
    );

    /* A pair's haystack is both brokers, both logins, its filename and every
       pair date it covers — so "705399 honor" finds it, and so does typing one
       of its rotation dates. */
    const pairs = (inv.pairs || []).filter((p) =>
      !active ||
      matches(
        hay([
          ...p.logins,
          ...p.label.map((x) => x.broker),
          p.filename,
          ...p.pairStartDates,
          p.current ? "current live" : "closed",
        ]),
        terms
      )
    );

    const pairDates = inv.pairDates.filter((p) =>
      !active ||
      matches(
        hay([
          p.pairStartDate,
          ...p.logins,
          ...p.usageIds,
          ...extrasFor(p.usageIds),
          ...whenParts(p.pairStart),
          ...whenParts(p.pairEnd),
          p.current ? "current open" : "closed",
        ]),
        terms
      )
    );

    const usages = inv.usages.filter((u) =>
      !active ||
      matches(
        hay([
          u.usageId,
          u.login,
          u.account,
          u.currency,
          u.role,
          u.host,
          u.port,
          u.leverage,
          u.pairStartDate,
          ...whenParts(u.pairStart),
          ...whenParts(u.pairEnd),
          ...whenParts(u.firstClose),
          ...whenParts(u.lastClose),
          u.current ? "current open" : "closed",
        ]),
        terms
      )
    );

    return {
      accounts,
      pairs,
      pairDates,
      usages,
      matched: accounts.length + pairs.length + pairDates.length + usages.length,
      active,
    };
  }, [inv, query]);

  const t = inv && inv.totals;

  return (
    <div className={`dev-root dev-theme-${theme}`} data-dev-theme={theme}>
      {/* Fixed behind everything at z-index -1, so it cannot intercept a click
          and cannot make an ancestor a scroll container. */}
      <div className="dev-glow" aria-hidden="true" />

      <header className="dev-topbar">
        <div className="dev-brand">
          <img src="/firstbench-logo.png" alt="FirstBench" className="dev-logo" />
          <div className="dev-titles">
            <h1 className="dev-title">Developer</h1>
            <p className="dev-sub">{user && user.username ? user.username : "signed in"}</p>
          </div>
        </div>

        {/* Between the brand and the controls so it takes the slack at wide
            widths and gets its own full-width row once the bar wraps. */}
        <div className="dev-search">
          <Search size={15} aria-hidden="true" className="dev-search-icon" />
          <input
            type="search"
            className="dev-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            /* Escape clears without reaching for the mouse. `type="search"` puts
               a native clear affordance in some browsers and not others, which
               is why there is an explicit button as well. */
            onKeyDown={(e) => {
              if (e.key === "Escape") setQuery("");
            }}
            placeholder="Search account, usage id, pair date, broker, host…"
            aria-label="Filter the cards below"
          />
          {query && (
            <>
              <span className="dev-search-count">{nf(view.matched)}</span>
              <button
                type="button"
                className="dev-search-clear"
                onClick={() => setQuery("")}
                aria-label="Clear the search"
                title="Clear"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        <div className="dev-topbar-actions">
          {/* IN THE HEADER, and first, because it governs everything below it —
              every card, every count and every download on the page. The list
              and the current choice both come from the response. */}
          <ContainerSelect
            containers={(inv && inv.containers) || []}
            value={
              inv && inv.selection
                ? inv.selection.mode === "all"
                  ? "all"
                  : inv.selection.containerId || ""
                : container
            }
            onChange={chooseContainer}
            includeAll
            label="Container"
            id="dev-container"
            busy={loading}
          />

          {/* Container management lives on its own page — it is configuration,
              not history, and mixing the two made this page confusing. No
              container is carried across: that page lists every one. */}
          {/* THE INVESTOR-FACING PAGES, so what a developer is configuring can be
              checked against what an investor actually sees, without typing a
              URL.

              NEW TAB on purpose: these are the pages being verified, and losing
              the dashboard — with its container selection and its search — to
              go and look at one is the wrong trade.

              THE REPORT CARRIES THE CONTAINER; THE PORTFOLIO DOES NOT. The
              report has a selector and honours ?container=, so "show me THIS
              container's report" is exactly the useful link. /investment-
              portfolio has no selector by decision and reads the DEFAULT
              container, so passing one would be ignored — and a link that looks
              like it selects something and does not is worse than no link. Its
              title says which container it will actually show. */}
          <a
            className="dev-ghost-btn"
            href={`/investor-report${container ? `?container=${encodeURIComponent(container)}` : ""}`}
            target="_blank"
            rel="noopener noreferrer"
            title={
              selectedName
                ? `Open the investor report for "${selectedName}" in a new tab`
                : "Open the investor report in a new tab"
            }
          >
            <ExternalLink size={15} aria-hidden="true" />
            <span>Report</span>
          </a>
          <a
            className="dev-ghost-btn"
            href="/investment-portfolio"
            target="_blank"
            rel="noopener noreferrer"
            title={
              defaultName
                ? `Open the portfolio page in a new tab — it always shows the default container, "${defaultName}"`
                : "Open the portfolio page in a new tab — it always shows the default container"
            }
          >
            <ExternalLink size={15} aria-hidden="true" />
            <span>Portfolio</span>
          </a>

          <Link to="/dev/containers" className="dev-ghost-btn" title="Create and edit containers">
            <Layers size={15} aria-hidden="true" />
            <span>Containers</span>
          </Link>
          <Link
            to={`/dev/accounts${container ? `?container=${encodeURIComponent(container)}` : ""}`}
            className="dev-ghost-btn"
            title="Brokers, users and accounts"
          >
            <Settings2 size={15} aria-hidden="true" />
            <span>Accounts</span>
          </Link>
          <button
            type="button"
            className="dev-icon-btn"
            onClick={toggle}
            aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
            title={dark ? "Light theme" : "Dark theme"}
          >
            {dark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
          </button>

          <button
            type="button"
            className="dev-icon-btn"
            onClick={load}
            disabled={loading}
            aria-label="Reload the inventory"
            title="Reload"
          >
            <RefreshCw size={16} aria-hidden="true" className={loading ? "dev-spin" : ""} />
          </button>

          <button type="button" className="dev-ghost-btn" onClick={handleLogout}>
            <LogOut size={15} aria-hidden="true" />
            <span>Sign out</span>
          </button>
        </div>
      </header>

      <main className="dev-main">
        {error && (
          <div className="dev-error" role="alert">
            <TriangleAlert size={16} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {/* OUTSIDE the `inv &&` gate below, deliberately. Container management does
            not depend on the inventory: if counting every deal in the database is
            slow or failing, creating a container or re-enabling a disabled one is
            exactly what somebody is here to do. It fetches its own list.

            SectionHead is passed in rather than exported, so the section's
            heading matches every other one on this page without that component
            having to become part of a module boundary. */}
        {loading && !inv && <p className="dev-note">Reading the stored history…</p>}

        {inv && (
          <>
            {/* ---------------------------- overview ---------------------------- */}
            <section className="dev-section">
              <SectionHead icon={Database} title="Overview">
                Read from the database — the real deals the broker reported, never the broker
                itself. Times are {inv.timezone}. Covering{" "}
                <strong>{t.firstClose || "—"}</strong> to <strong>{t.lastClose || "—"}</strong>.
                {view.active && " These totals and this download cover the whole database; the search filters the sections below."}
              </SectionHead>

              {/* Each tile is a LINK to the rows it counted. The third element is
                  the browse kind, kept beside the count rather than derived from the
                  label: a label is prose and will be reworded, and a route built out
                  of prose breaks silently when it is. */}
              <div className="dev-tiles">
                {[
                  ["accounts", t.logins, "accounts"],
                  ["account usages", t.usages, "usages"],
                  ["pairs", t.pairs, "pairs"],
                  ["pair dates", t.pairDates, "pairs"],
                  /* deals = trades + cash + other, and it is shown BESIDE its
                     parts on purpose. Seeing 376 trades next to a 393-row export
                     invites the question of what happened to the other 17; the
                     answer is the two tiles either side of this one. */
                  ["deals, all kinds", t.deals, "deals"],
                  ["trades", t.trades, "trades"],
                  ["cash movements", t.cash, "cash"],
                  ["settlements", t.settlements, "settlements"],
                ].map(([label, n, kind]) => (
                  <Link
                    className="dev-tile dev-tile-link"
                    key={label}
                    /* THE CONTAINER TRAVELS WITH THE CLICK. This tile's count
                       is one container's; a listing that dropped it would open
                       showing every container's rows under a heading taken from
                       this number, and the two would silently disagree. */
                    to={"/dev/browse/" + kind + (container ? `?container=${encodeURIComponent(container)}` : "")}
                    title={"List all " + nf(n) + " " + label}
                  >
                    <span className="dev-tile-n">{nf(n)}</span>
                    <span className="dev-tile-l">{label}</span>
                    <ChevronRight className="dev-tile-go" size={14} aria-hidden="true" />
                  </Link>
                ))}
              </div>

              {t.other > 0 && (
                <p className="dev-warn">
                  <TriangleAlert size={15} aria-hidden="true" />
                  <span>
                    {nf(t.other)} deal(s) are neither a trade nor a cash movement. They appear on
                    the <em>Other deals</em> sheet of an Everything download.
                  </span>
                </p>
              )}

              {inv.orphanUsageIds && inv.orphanUsageIds.length > 0 && (
                <p className="dev-warn">
                  <TriangleAlert size={15} aria-hidden="true" />
                  <span>
                    {inv.orphanUsageIds.length} usage id(s) have deals but no account-usage row:{" "}
                    <code>{inv.orphanUsageIds.join(", ")}</code>. They are included in the
                    all-accounts downloads but in none of the scoped ones.{" "}
                    <code>scripts/mt5-retag.js</code> is the tool for this.
                  </span>
                </p>
              )}

              <div className="dev-card dev-card-wide">
                <div className="dev-card-head">
                  <h3 className="dev-card-title">All accounts</h3>
                  <span className="dev-tag">every usage, every deal</span>
                </div>
                <ShapeList
                  counts={{
                    usages: t.usages,
                    trades: t.trades,
                    cash: t.cash,
                    settlements: t.settlements,
                  }}
                  downloads={inv.downloads}
                  onDownload={onDownload}
                  busy={busy}
                  extra={
                    <div className="dev-shape">
                      <span
                        className="dev-shape-label"
                        title="everything, plus one trade sheet per account usage"
                      >
                        Full workbook
                      </span>
                      <span className="dev-pill">{nf(t.usages)} sheets</span>
                      <span className="dev-actions">
                        <button
                          type="button"
                          className="dev-btn"
                          disabled={busy === inv.downloads.full.xlsx}
                          onClick={() => onDownload(inv.downloads.full.xlsx)}
                          title="Download the full workbook as Excel"
                        >
                          <Download size={13} aria-hidden="true" />
                          <span>{busy === inv.downloads.full.xlsx ? "…" : "Excel"}</span>
                        </button>
                      </span>
                    </div>
                  }
                />
              </div>
            </section>

            {/* ---------------------------- by account -------------------------- */}
            <section className="dev-section">
              <SectionHead
                icon={Users}
                title="By account"
                shown={view.accounts.length}
                total={inv.accounts.length}
              >
                One card per login, covering <strong>every</strong> usage of that account. A login
                reused under a later pair date has several usages, and this is the download that
                spans them.
              </SectionHead>

              {view.accounts.length === 0 && <NoMatches what="accounts" />}
              <div className="dev-grid">
                {view.accounts.map((a) => (
                  <article className="dev-card" key={a.login}>
                    <div className="dev-card-head">
                      <h3 className="dev-card-title dev-mono">{a.login}</h3>
                      <span className="dev-tag">{a.usageCount} usage(s)</span>
                    </div>
                    <p className="dev-card-text">{a.account || "unnamed"}</p>
                    <p className="dev-card-when" title={`${a.firstClose} → ${a.lastClose}`}>
                      {shortWhen(a.firstClose)} → {shortWhen(a.lastClose)}
                    </p>
                    <ShapeList
                      counts={{ ...a.counts, usages: a.usageCount }}
                      downloads={a.downloads}
                      onDownload={onDownload}
                      busy={busy}
                    />
                  </article>
                ))}
              </div>
            </section>

            {/* --------------------------- by pair date ------------------------- */}
            {/* ------------------------------ BY PAIR ------------------------
                The same two accounts, every period they traded together. The
                section below this one is unchanged and still lists each pair
                DATE on its own — a rotation is still downloadable by itself.

                This one answers the other question: 2711224 + 4032609 rotated
                five times, and as five downloads there is no way to read the
                pair as a whole. One file, with a Periods sheet naming each
                rotation inside it. */}
            <section className="dev-section">
              <SectionHead icon={Layers} title="By pair" shown={view.pairs.length} total={(inv.pairs || []).length}>
                The same accounts, across <em>every</em> period they traded together — a pair that
                rotated five times is one download. Where one account handed over to another
                part-way through a period, the deals are split by who was actually trading
                alongside them, so each pair holds only its own.
              </SectionHead>

              {view.pairs.length === 0 && <NoMatches what="pairs" />}
              <div className="dev-grid">
                {view.pairs.map((p) => (
                  <article className="dev-card" key={p.key}>
                    <div className="dev-card-head">
                      <h3 className="dev-card-title dev-mono-sm">{p.logins.join(" · ")}</h3>
                      <span className={`dev-badge${p.current ? " dev-badge-live" : ""}`}>
                        {p.current ? "current" : "closed"}
                      </span>
                    </div>
                    {/* The broker beside each login, because two accounts at the
                        same broker are otherwise indistinguishable — 7000351 and
                        7000404 are both Honor Capital. */}
                    <p className="dev-card-text">
                      {p.label.map((x) => `${x.broker || "unknown"} ${x.login}`).join("  ·  ")}
                    </p>
                    <p className="dev-card-when" title={p.pairStartDates.join(", ")}>
                      {p.pairDateCount} period{p.pairDateCount === 1 ? "" : "s"} ·{" "}
                      {shortWhen(p.firstStart)} → {p.lastEnd ? shortWhen(p.lastEnd) : "still open"}
                    </p>
                    {p.split && (
                      <p className="dev-card-gap">
                        <TriangleAlert size={13} aria-hidden="true" /> shares a period with another
                        pair — one account handed over mid-period, and only its own trades are here
                      </p>
                    )}
                    {p.accountCount !== 2 && (
                      <p className="dev-card-gap">
                        <TriangleAlert size={13} aria-hidden="true" /> {p.accountCount} accounts
                        traded together here, not two
                      </p>
                    )}
                    <ShapeList
                      counts={{ ...p.counts, usages: p.usageCount }}
                      downloads={p.downloads}
                      onDownload={onDownload}
                      busy={busy}
                    />

                    <div className="dev-inner">
                      <span className="dev-inner-title">The periods inside it</span>
                      {p.pairStartDates.map((d) => {
                        const pd = inv.pairDates.find((x) => x.pairStartDate === d);
                        return (
                          <div className="dev-inner-row" key={d}>
                            <span className="dev-inner-id dev-mono">{d}</span>
                            <span className="dev-actions">
                              {pd && (
                                <button
                                  type="button"
                                  className="dev-btn dev-btn-sm"
                                  disabled={busy === pd.downloads.everything.xlsx}
                                  onClick={() => onDownload(pd.downloads.everything.xlsx)}
                                  title={`Just this period — ${pd.counts.trades} trade(s)`}
                                >
                                  xlsx
                                </button>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section className="dev-section">
              <SectionHead
                icon={CalendarDays}
                title="By pair date"
                shown={view.pairDates.length}
                total={inv.pairDates.length}
              >
                The accounts that traded together under one <code>MT5_PAIR_START_DATE</code>. The
                pair download holds all of them; each account inside it is also listed separately,
                because one account in one pair date is exactly one account usage.
              </SectionHead>

              {view.pairDates.length === 0 && <NoMatches what="pair dates" />}
              <div className="dev-grid">
                {view.pairDates.map((p) => (
                  <article className="dev-card" key={p.pairStartDate}>
                    <div className="dev-card-head">
                      <h3 className="dev-card-title dev-mono">{p.pairStartDate}</h3>
                      <span className={`dev-badge${p.current ? " dev-badge-live" : ""}`}>
                        {p.current ? "current" : "closed"}
                      </span>
                    </div>
                    <p className="dev-card-text dev-mono-sm">{p.logins.join(" · ")}</p>
                    <p className="dev-card-when" title={`${p.pairStart} → ${p.pairEnd || "open"}`}>
                      {shortWhen(p.pairStart)} → {p.pairEnd ? shortWhen(p.pairEnd) : "still open"}
                    </p>
                    <ShapeList
                      counts={{ ...p.counts, usages: p.accountCount }}
                      downloads={p.downloads}
                      onDownload={onDownload}
                      busy={busy}
                    />

                    <div className="dev-inner">
                      <span className="dev-inner-title">Each account in this pair</span>
                      {p.usageIds.map((id) => {
                        const u = inv.usages.find((x) => x.usageId === id);
                        if (!u) return null;
                        return (
                          <div className="dev-inner-row" key={id}>
                            <span className="dev-inner-id" title={id}>
                              {id}
                            </span>
                            <span className="dev-actions">
                              <button
                                type="button"
                                className="dev-btn dev-btn-sm"
                                disabled={busy === u.downloads.everything.xlsx}
                                onClick={() => onDownload(u.downloads.everything.xlsx)}
                                title={`Download everything for ${id} as Excel`}
                              >
                                <Download size={12} aria-hidden="true" />
                                <span>Excel</span>
                              </button>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </article>
                ))}
              </div>
            </section>

            {/* -------------------------- by account usage ---------------------- */}
            <section className="dev-section">
              <SectionHead
                icon={Layers}
                title="By account usage"
                shown={view.usages.length}
                total={inv.usages.length}
              >
                One card per <code>usageId</code>, which is{" "}
                <code>&lt;login&gt;_&lt;pairStartDate&gt;</code>. Each usage has its own opening
                balance and its own P&amp;L — the same login under a later pair date starts fresh.
              </SectionHead>

              {view.usages.length === 0 && <NoMatches what="account usages" />}
              <div className="dev-grid">
                {view.usages.map((u) => (
                  <article className="dev-card" key={u.usageId}>
                    <div className="dev-card-head">
                      <h3 className="dev-card-title dev-mono" title={u.usageId}>
                        {u.usageId}
                      </h3>
                      <span className={`dev-badge${u.current ? " dev-badge-live" : ""}`}>
                        {u.current ? "current" : "closed"}
                      </span>
                    </div>
                    <p className="dev-card-text">{u.account || "unnamed"}</p>

                    <dl className="dev-facts">
                      <div>
                        <dt>Opening</dt>
                        <dd className="dev-num">{money(u.openingBalance)}</dd>
                      </div>
                      <div>
                        <dt>Last</dt>
                        <dd className="dev-num">{money(u.lastBalance)}</dd>
                      </div>
                      <div>
                        <dt>Currency</dt>
                        <dd>{u.currency}</dd>
                      </div>
                    </dl>

                    {/* Shown because the search matches on it. A card that turns
                        up for a term found nowhere on it is indistinguishable
                        from a broken filter. */}
                    {u.host && (
                      <p className="dev-card-when" title={`${u.host}:${u.port}`}>
                        {u.host}
                        {u.leverage ? ` · ${u.leverage}` : ""}
                      </p>
                    )}

                    <p className="dev-card-when" title={`${u.pairStart} → ${u.pairEnd || "open"}`}>
                      {shortWhen(u.pairStart)} → {u.pairEnd ? shortWhen(u.pairEnd) : "still open"}
                    </p>
                    <ShapeList
                      counts={{ ...u.counts, usages: 1 }}
                      downloads={u.downloads}
                      onDownload={onDownload}
                      busy={busy}
                      />
                  </article>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default DeveloperDashboard;
